import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, getToken } from '../api/client';
import { urlImpression } from '../api/config';
import { Badge, Bandeau, Carte, Modale, Squelette, Vide } from '../components/ui';
import { dateFr, montant, nomClient } from '../lib/format';
import { T } from '../i18n';

interface Facture {
  _id: string; numero: string; type: string; date: string; totalMinor: number; devise: string;
  tauxTvaPct?: number; acompteMinor?: number;
  customer?: { nom: string; prenom?: string; raisonSociale?: string };
  contract?: { numero: string };
}
interface ContratLeger {
  _id: string; numero: string; totalGeneralMinor: number; totalEncaisseMinor: number; soldeMinor: number; statut: string;
  customer?: { nom: string; prenom?: string; raisonSociale?: string };
}

export default function Factures() {
  const [liste, setListe] = useState<Facture[] | null>(null);
  const [contrats, setContrats] = useState<ContratLeger[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState('');  /* La TVA se decide facture par facture : une societe la demande, un
   * particulier qui paie au comptoir ne la veut pas. Le taux est fige sur
   * la facture au moment ou elle est etablie. */
  const [avecTva, setAvecTva] = useState(true);


  const charger = useCallback(() => {
    api.get<Facture[]>('/factures').then(setListe);
    api.get<ContratLeger[]>('/contrats').then(setContrats);
  }, []);
  useEffect(charger, [charger]);
  if (!liste) return <Squelette lignes={6} />;
  const L = T.factures;

  async function emettre(contract: string, type: 'acompte' | 'solde') {
    setErreur('');
    try { const f = await api.post<Facture>('/factures', { contract, type, avecTva }); setOuvert(false); charger();
      window.open(urlImpression(`/factures/${f._id}/imprimer`, getToken()), '_blank'); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : L.erreur); }
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{L.titre}</h1>
          <p>{L.sousTitre}</p>
        </div>
        <div className="actions"><button className="btn btn-p" onClick={() => setOuvert(true)}>{L.emettre}</button></div>
      </div>

      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre={L.vide} /></div> : (
          <table className="tbl">
            <thead><tr><th>{L.colNumero}</th><th>{L.colDate}</th><th>{L.colType}</th><th>{L.colClient}</th><th>{L.colContrat}</th><th className="droite">{L.colTotal}</th><th /></tr></thead>
            <tbody>
              {liste.map((f) => (
                <tr key={f._id}>
                  <td><b>{f.numero}</b></td>
                  <td className="num">{dateFr(f.date)}</td>
                  <td><Badge statut={f.type === 'avoir' ? 'retard' : f.type === 'acompte' ? 'confirmee' : 'en_cours'} libelle={L.types[f.type] ?? f.type} /></td>
                  <td>{nomClient(f.customer)}</td>
                  <td className="mini sourdine">{f.contract?.numero ?? '—'}</td>
                  {/* La colonne montre ce que le client doit payer : HT + TVA, comme le document. */}
                  <td className="droite montant">
                    {montant(f.totalMinor + Math.round((f.totalMinor * (f.tauxTvaPct ?? 0)) / 100), f.devise)}
                    {(f.tauxTvaPct ?? 0) > 0 && <div className="mini sourdine">{L.dontTva(f.tauxTvaPct)}</div>}
                    {(f.acompteMinor ?? 0) > 0 && (
                      <div className="mini sourdine">{L.dejaVerse(montant(f.acompteMinor ?? 0, f.devise))}</div>
                    )}
                  </td>
                  <td className="droite">
                    <a className="btn btn-s" target="_blank" rel="noreferrer" href={urlImpression(`/factures/${f._id}/imprimer`, getToken())}>{L.imprimer}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Carte>

      {ouvert && (
        <Modale titre={L.emettre} onFermer={() => setOuvert(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            {L.explication}
          </p>
          <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 12 }}>
            <input type="checkbox" checked={avecTva} onChange={(e) => setAvecTva(e.target.checked)} />
            {L.avecTva}
            <span className="mini sourdine">
              {L.avecTvaAide}
            </span>
          </label>

          <div style={{ display: 'grid', gap: 8, maxHeight: '55vh', overflowY: 'auto' }}>
            {contrats.map((c) => (
              <div key={c._id} style={{ border: '1px solid var(--bord)', borderRadius: 'var(--r-champ)', padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <b>{c.numero}</b> <Badge statut={c.statut} />
                    <div className="mini sourdine">{nomClient(c.customer)} · {L.solde(montant(c.soldeMinor))}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-s" disabled={c.totalEncaisseMinor <= 0} onClick={() => void emettre(c._id, 'acompte')}>{L.btnAcompte}</button>
                    <button className="btn btn-s btn-p" onClick={() => void emettre(c._id, 'solde')}>{L.btnSolde}</button>

                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modale>
      )}
    </>
  );
}
